// Web push in this browser: whether it can be on, and turning it on or off. On: the browser asks permission, the
// service worker subscribes with the server's public key, and the subscription is registered (POST /api/devices).
// Off: unsubscribe and forget it. Each browser (and the installed app on a phone) is its own device.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

export type PushState = 'loading' | 'unsupported' | 'install' | 'server_off' | 'blocked' | 'off' | 'on';

/** The words for each state, as the Settings row says them. */
export const PUSH_WORDS: Record<PushState, string> = {
  loading: 'Checking',
  unsupported: 'This browser cannot show push notifications.',
  install: 'On iPhone and iPad, add the app to your Home Screen (Share, then Add to Home Screen), open it from there, and turn push on in it.',
  server_off: 'Push is not set up on this server yet.',
  blocked: 'Notifications are blocked for this site. Allow them in the browser\'s site settings, then turn push on.',
  off: 'Off in this browser.',
  on: 'On in this browser.',
};

const toBytes = (b64u: string) => {
  const s = atob(b64u.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64u.length % 4)) % 4));
  return Uint8Array.from(s, c => c.charCodeAt(0));
};
const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array) => !!a && a.byteLength === b.length && new Uint8Array(a).every((x, i) => x === b[i]);
const supported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const appleTouch = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration('/')) ?? navigator.serviceWorker.register('/sw.js');
}

/** This browser's push: its state, and turning it on or off. */
export function usePush() {
  const [state, setState] = useState<PushState>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!supported()) { setState(appleTouch() ? 'install' : 'unsupported'); return; }
    try {
      const st = await api.push.status();
      if (!st.publicKey) { setState('server_off'); return; }
      if (Notification.permission === 'denied') { setState('blocked'); return; }
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      if (!sub || Notification.permission !== 'granted') { setState('off'); return; }
      // Made with another server key (the keys were changed): it can never be delivered, so start again.
      if (!sameKey(sub.options.applicationServerKey, toBytes(st.publicKey))) { await sub.unsubscribe().catch(() => false); setState('off'); return; }
      // Tell the server again (it may have dropped it, or this is another person signed in on this browser).
      await api.devices.register('web', JSON.stringify(sub.toJSON()));
      setState('on');
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); setState('off'); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const turnOn = async () => {
    setBusy(true); setError('');
    try {
      const st = await api.push.status();
      if (!st.publicKey) { setState('server_off'); return; }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'blocked' : 'off'); return; }
      const reg = await registration();
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(st.publicKey) });
      await api.devices.register('web', JSON.stringify(sub.toJSON()));
      setState('on');
    } catch (e) {
      setError(`Push could not be turned on: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setBusy(false); }
  };

  const turnOff = async () => {
    setBusy(true); setError('');
    try {
      const sub = await (await navigator.serviceWorker.getRegistration('/'))?.pushManager.getSubscription();
      if (sub) {
        const token = JSON.stringify(sub.toJSON());
        await sub.unsubscribe();
        await api.devices.forget('web', token);
      }
      setState('off');
    } catch (e) { setError(`Push could not be turned off: ${e instanceof Error ? e.message : String(e)}`); } finally { setBusy(false); }
  };

  return { state, busy, error, turnOn, turnOff, refresh };
}

/** Where sw-open.js keeps the page a tapped notification should open (Cache Storage, shared with the worker). */
const PENDING_CACHE = 'dpw-open', PENDING_KEY = '/__dpw/open', PENDING_MS = 120_000;
/** A path of this app ("/activities/x"), never "//host" or another origin. */
export const appPath = (p: unknown): p is string => typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/\\');

/** The page a tapped notification left for this window, once: null when there is none, or it is over two minutes old. */
export async function takePendingOpen(): Promise<string | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const c = await caches.open(PENDING_CACHE);
    const r = await c.match(PENDING_KEY);
    if (!r) return null;
    await c.delete(PENDING_KEY);
    const v = await r.json() as { path?: unknown; at?: unknown };
    // The age on the clock the worker used (sw-open.js wallMs: performance, not Date).
    const age = performance.timeOrigin + performance.now() - (typeof v.at === 'number' ? v.at : 0);
    return appPath(v.path) && age < PENDING_MS ? v.path : null;
  } catch { return null; }
}

/**
 * Messages from the service worker: a tapped notification opens its page in this window (routed in place, no reload),
 * and a renewed subscription is registered again. Also, on start and whenever the app comes back to the front, the
 * page a tap left (sw.js keeps it first, for a window that was still starting or asleep when the tap came). Mounted
 * once, in the signed-in frame.
 */
export function usePushMessages() {
  const nav = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const go = (path: string) => { if (`${location.pathname}${location.search}${location.hash}` !== path) nav(path); };
    const pending = () => { void takePendingOpen().then(p => { if (p) go(p); }); };
    pending();
    const vis = () => { if (document.visibilityState === 'visible') pending(); };
    document.addEventListener('visibilitychange', vis);
    const on = (e: MessageEvent) => {
      const m = e.data as { type?: string; link?: string } | null;
      if (m?.type === 'open' && appPath(m.link)) { void takePendingOpen(); go(m.link); }
      if (m?.type === 'resubscribe') {
        void (async () => {
          const st = await api.push.status();
          const reg = await navigator.serviceWorker.getRegistration('/');
          if (!st.publicKey || !reg || Notification.permission !== 'granted') return;
          const sub = (await reg.pushManager.getSubscription()) ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(st.publicKey) });
          await api.devices.register('web', JSON.stringify(sub.toJSON()));
        })().catch(() => undefined);
      }
    };
    navigator.serviceWorker.addEventListener('message', on);
    // Messages sent while the app was starting wait until this listener is ready.
    navigator.serviceWorker.startMessages();
    return () => { navigator.serviceWorker.removeEventListener('message', on); document.removeEventListener('visibilitychange', vis); };
  }, [nav]);
}
